export const SHOP_PRODUCT_REQUIRED_FIELD_LABELS = {
  name: 'Nome do produto',
  slug: 'Slug',
  gender: 'Gênero',
  brand: 'Marca',
  weight: 'Peso',
  fullPrice: 'Preço cheio',
  price: 'Preço',
  subscriberDiscountPercentage: 'Desconto de assinante',
  metadataTitle: 'Título',
  metadataDescription: 'Descrição',
  productDescription: 'Descrição do produto',
  images: 'Imagem',
  category: 'Categoria',
  groupId: 'Grupo',
  validity: 'Vigente',
  type: 'Tipo do produto',
  EAN: 'EAN',
  NCM: 'NCM'
} as const;

export type ShopProductRequiredFieldKey =
  keyof typeof SHOP_PRODUCT_REQUIRED_FIELD_LABELS;

export interface ShopProductRequiredFields {
  name: string | null | undefined;
  slug: string | null | undefined;
  gender: string | null | undefined;
  brand: string | null | undefined;
  weight: number | null | undefined;
  fullPrice: number | null | undefined;
  price: number | null | undefined;
  subscriberDiscountPercentage: number | null | undefined;
  metadataTitle: string | null | undefined;
  metadataDescription: string | null | undefined;
  productDescription: string | null | undefined;
  images: string[] | null | undefined;
  categoryId: string | null | undefined;
  groupId: number | null | undefined;
  validity: boolean | null | undefined;
  type: string | null | undefined;
  EAN: string | null | undefined;
  NCM: string | null | undefined;
}

export const SHOP_PRODUCT_REQUIRED_CONTROL_PATHS: Partial<
  Record<ShopProductRequiredFieldKey, string>
> = {
  name: 'name',
  slug: 'slug',
  gender: 'gender',
  brand: 'brand',
  weight: 'weight',
  fullPrice: 'price.fullPrice',
  price: 'price.price',
  subscriberDiscountPercentage: 'price.subscriberDiscountPercentage',
  metadataTitle: 'metadata.title',
  metadataDescription: 'metadata.description',
  images: 'images',
  category: 'category',
  groupId: 'groupId',
  validity: 'validity',
  type: 'type',
  EAN: 'EAN',
  NCM: 'NCM'
};

export const SHOP_PRODUCT_GENERIC_REQUIRED_ERROR =
  'Preencha todos os campos obrigatórios antes de salvar.';

export function isValidProductGroupId(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value > 0;
}

export function toProductGroupId(value: unknown): number | null {
  const groupId = Number(value);
  return isValidProductGroupId(groupId) ? groupId : null;
}

export function resolveProductGroupId(
  groups: Array<{ value?: number }> | null | undefined,
  groupId: unknown
): number | null {
  const id = toProductGroupId(groupId);
  if (id == null) return null;
  return (groups ?? []).some((group) => group.value === id) ? id : null;
}

const VALID_GENDERS = ['male', 'female', 'unisex'];

function isFilledText(value: string | null | undefined): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function toFilledCode(value: unknown): string | null {
  if (value == null || typeof value === 'boolean' || typeof value === 'object') {
    return null;
  }
  const text = String(value).trim();
  return text.length > 0 ? text : null;
}

export function toCategoryId(value: unknown): string | null {
  if (value == null || typeof value === 'boolean') return null;
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return String(value);
  }
  if (typeof value === 'string') {
    const text = value.trim();
    return text.length > 0 ? text : null;
  }
  if (typeof value === 'object' && 'id' in (value as object)) {
    return toCategoryId((value as { id?: unknown }).id);
  }
  return null;
}

export function isFilledHtml(value: string | null | undefined): boolean {
  if (typeof value !== 'string') return false;
  return (
    value
      .replace(/<br\s*\/?>/gi, ' ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim().length > 0
  );
}

export function hasShopProductImage(
  images: string[] | null | undefined
): boolean {
  return !!images?.some(
    (image) => typeof image === 'string' && image.trim().length > 0
  );
}

function isPositiveAmount(value: number | null | undefined): boolean {
  return typeof value === 'number' && !Number.isNaN(value) && value >= 0.01;
}

function isDiscountPercentage(value: number | null | undefined): boolean {
  return (
    typeof value === 'number' &&
    !Number.isNaN(value) &&
    value >= 0 &&
    value <= 100
  );
}

export function toShopProductRequiredFields(formValue: {
  name?: string | null;
  slug?: string | null;
  gender?: string | null;
  brand?: string | null;
  weight?: number | null;
  price?: {
    fullPrice?: number | null;
    price?: number | null;
    subscriberDiscountPercentage?: number | null;
  } | null;
  metadata?: {
    title?: string | null;
    description?: string | null;
  } | null;
  productDescription?: string | null;
  images?: string[] | null;
  category?: unknown;
  categoryId?: string | number | null;
  groupId?: number | null;
  validity?: boolean | null;
  type?: string | null;
  EAN?: string | number | null;
  NCM?: string | number | null;
}): ShopProductRequiredFields {
  return {
    name: formValue.name,
    slug: formValue.slug,
    gender: formValue.gender,
    brand: formValue.brand,
    weight: formValue.weight,
    fullPrice: formValue.price?.fullPrice,
    price: formValue.price?.price,
    subscriberDiscountPercentage: formValue.price?.subscriberDiscountPercentage,
    metadataTitle: formValue.metadata?.title,
    metadataDescription: formValue.metadata?.description,
    productDescription: formValue.productDescription,
    images: formValue.images,
    categoryId:
      toCategoryId(formValue.category) ?? toCategoryId(formValue.categoryId),
    groupId: toProductGroupId(formValue.groupId),
    validity:
      typeof formValue.validity === 'boolean' ? formValue.validity : null,
    type: formValue.type ?? null,
    EAN: toFilledCode(formValue.EAN),
    NCM: toFilledCode(formValue.NCM)
  };
}

export function getInvalidShopProductRequiredFields(
  fields: ShopProductRequiredFields,
  options: { forPublish?: boolean; hasVariants?: boolean } = {}
): ShopProductRequiredFieldKey[] {
  const invalid: ShopProductRequiredFieldKey[] = [];

  if (!isFilledText(fields.name)) invalid.push('name');
  if (!isFilledText(fields.slug)) invalid.push('slug');
  if (!isFilledText(fields.gender) || !VALID_GENDERS.includes(fields.gender)) {
    invalid.push('gender');
  }
  if (!isFilledText(fields.brand)) invalid.push('brand');
  if (!isPositiveAmount(fields.weight)) invalid.push('weight');
  if (!isPositiveAmount(fields.fullPrice)) invalid.push('fullPrice');
  if (!isPositiveAmount(fields.price)) invalid.push('price');
  if (!isDiscountPercentage(fields.subscriberDiscountPercentage)) {
    invalid.push('subscriberDiscountPercentage');
  }
  if (!isFilledText(fields.metadataTitle)) invalid.push('metadataTitle');
  if (!isFilledText(fields.metadataDescription)) {
    invalid.push('metadataDescription');
  }
  if (!isFilledHtml(fields.productDescription)) {
    invalid.push('productDescription');
  }
  if (!isFilledText(fields.categoryId)) invalid.push('category');
  if (!options.hasVariants && !isValidProductGroupId(fields.groupId)) {
    invalid.push('groupId');
  }
  if (!options.hasVariants && typeof fields.validity !== 'boolean') {
    invalid.push('validity');
  }
  if (!isFilledText(fields.type)) invalid.push('type');
  if (!isFilledText(fields.EAN)) invalid.push('EAN');
  if (!isFilledText(fields.NCM)) invalid.push('NCM');
  if (options.forPublish && !hasShopProductImage(fields.images)) {
    invalid.push('images');
  }

  return invalid;
}

export function canPublishShopProduct(
  fields: ShopProductRequiredFields
): boolean {
  return (
    getInvalidShopProductRequiredFields(fields, { forPublish: true }).length ===
    0
  );
}

export function getInvalidShopProductRequiredControlPaths(
  invalidFields: ShopProductRequiredFieldKey[]
): string[] {
  return invalidFields.flatMap((field) => {
    const path = SHOP_PRODUCT_REQUIRED_CONTROL_PATHS[field];
    return path ? [path] : [];
  });
}

export function isShopProductRequiredControlInvalid(
  path: string,
  invalidFields: ShopProductRequiredFieldKey[]
): boolean {
  return getInvalidShopProductRequiredControlPaths(invalidFields).includes(
    path
  );
}

export function getShopProductRequiredFieldsError(
  invalidFields: ShopProductRequiredFieldKey[]
): string {
  if (!invalidFields.length) {
    return SHOP_PRODUCT_GENERIC_REQUIRED_ERROR;
  }

  const labels = invalidFields.map(
    (field) => SHOP_PRODUCT_REQUIRED_FIELD_LABELS[field]
  );
  return `Preencha ou corrija os campos obrigatórios: ${labels.join(', ')}.`;
}

export function getShopProductPublishError(
  invalidFields: ShopProductRequiredFieldKey[]
): string {
  return `O produto só pode ser publicado com todos os campos obrigatórios preenchidos. ${getShopProductRequiredFieldsError(invalidFields)}`;
}

export function getShopProductSaveError(
  published: boolean,
  invalidFields: ShopProductRequiredFieldKey[],
  formInvalid: boolean
): string | null {
  if (!published) {
    return null;
  }
  if (invalidFields.length) {
    return getShopProductPublishError(invalidFields);
  }
  if (formInvalid) {
    return SHOP_PRODUCT_GENERIC_REQUIRED_ERROR;
  }
  return null;
}

export function resolveBrandCategoryValue(
  brands: Array<{ name?: string }> | null | undefined,
  selectedBrand: string | null | undefined
): string | null {
  const brandName = selectedBrand?.trim();
  if (!brandName) return null;
  const normalized = brandName.toLowerCase();
  return (
    (brands ?? []).find(
      (brand) => brand.name?.trim().toLowerCase() === normalized
    )?.name ?? null
  );
}

export function parseShopProductPublished(value: unknown): boolean | undefined {
  if (value === true || value === 1) return true;
  if (value === false || value === 0) return false;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (!normalized) return undefined;
    if (['true', '1', 'yes', 'sim', 'verdadeiro'].includes(normalized)) {
      return true;
    }
    if (['false', '0', 'no', 'nao', 'não', 'falso'].includes(normalized)) {
      return false;
    }
  }
  return undefined;
}

export function getBrandCategoryRequiredError(
  brands: Array<{ name?: string }> | null | undefined,
  selectedBrand: string | null | undefined,
  interacted: boolean
): { required: true } | null {
  if (resolveBrandCategoryValue(brands, selectedBrand)) {
    return null;
  }
  if (!interacted) {
    return null;
  }
  return { required: true };
}

export const SHOP_PRODUCT_UI_ONLY_FIELDS = [
  'categoryTree',
  'validade',
  'estoqueLote'
] as const;

export function omitShopProductUiOnlyFields<T extends Record<string, unknown>>(
  product: T
): T {
  for (const field of SHOP_PRODUCT_UI_ONLY_FIELDS) {
    delete product[field];
  }
  return product;
}

export function sanitizeShopProductUpdatePayload<T extends Record<string, unknown>>(
  product: T
): T {
  omitShopProductUiOnlyFields(product);
  if ('groupId' in product && !isValidProductGroupId(product['groupId'])) {
    delete product['groupId'];
  }
  return product;
}

export interface ShopProductRequiredSource {
  name?: string | null;
  slug?: string | null;
  gender?: string | null;
  brand?: string | null;
  weight?: number | null;
  price?: {
    fullPrice?: number | null;
    price?: number | null;
    subscriberDiscountPercentage?: number | null;
  } | null;
  metadata?: {
    title?: string | null;
    description?: string | null;
  } | null;
  description?: {
    description?: string | null;
  } | null;
  images?: string[] | null;
  category?: { id?: string | number | null } | null;
  categoryId?: string | number | null;
  groupId?: number | null;
  validity?: boolean | null;
  type?: string | null;
  EAN?: string | number | null;
  NCM?: string | number | null;
  hasVariants?: boolean | null;
}

export function willShopProductBePublished(
  existingPublished: boolean | null | undefined,
  updatePublished: boolean | null | undefined
): boolean {
  if (updatePublished === false) return false;
  if (updatePublished === true) return true;
  return !!existingPublished;
}

export interface ShopProductParentPersistSource {
  type?: string | null;
  NCM?: string | null;
  brand?: string | null;
  category?: { id?: string | number | null } | null;
  categoryId?: string | number | null;
  published?: boolean | null;
}

export interface ShopProductParentPersistFields {
  type?: string;
  NCM?: string;
  brand?: string;
  categoryId?: string;
  published?: boolean;
}

function isPersistableParentText(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

export function pickShopProductParentPersistFields<
  T extends ShopProductParentPersistSource
>(persistData: T): ShopProductParentPersistFields {
  const picked: ShopProductParentPersistFields = {};
  if ('type' in persistData && isPersistableParentText(persistData.type)) {
    picked.type = persistData.type.trim();
  }
  if ('NCM' in persistData && isPersistableParentText(persistData.NCM)) {
    picked.NCM = persistData.NCM.trim();
  }
  if ('brand' in persistData && isPersistableParentText(persistData.brand)) {
    picked.brand = persistData.brand.trim();
  }
  const categoryId =
    toCategoryId(persistData.categoryId) ?? toCategoryId(persistData.category);
  if (categoryId) {
    picked.categoryId = categoryId;
  }
  return picked;
}

export function getShopProductVariantParentSiblingUpdate<
  T extends ShopProductParentPersistSource
>(persistData: T): ShopProductParentPersistFields {
  const parentFields = pickShopProductParentPersistFields(persistData);
  if ('published' in persistData && persistData.published != null) {
    parentFields.published = persistData.published;
  }
  return parentFields;
}

export function mergeShopProductRequiredFields(
  existing: ShopProductRequiredSource,
  update: ShopProductRequiredSource
): ShopProductRequiredFields {
  return toShopProductRequiredFields({
    name: update.name ?? existing.name,
    slug: update.slug ?? existing.slug,
    gender: update.gender ?? existing.gender,
    brand: update.brand ?? existing.brand,
    weight: update.weight ?? existing.weight,
    price: {
      fullPrice: update.price?.fullPrice ?? existing.price?.fullPrice,
      price: update.price?.price ?? existing.price?.price,
      subscriberDiscountPercentage:
        update.price?.subscriberDiscountPercentage ??
        existing.price?.subscriberDiscountPercentage
    },
    metadata: {
      title: update.metadata?.title ?? existing.metadata?.title,
      description:
        update.metadata?.description ?? existing.metadata?.description
    },
    productDescription:
      update.description?.description ?? existing.description?.description,
    images: update.images ?? existing.images,
    category: update.category ?? existing.category,
    categoryId: update.categoryId ?? existing.categoryId,
    groupId: update.groupId ?? existing.groupId,
    validity: update.validity ?? existing.validity,
    type: update.type ?? existing.type,
    EAN: update.EAN ?? existing.EAN,
    NCM: update.NCM ?? existing.NCM
  });
}

export function getShopProductBatchPublishError(
  ean: string,
  brands: Array<{ name?: string }> | null | undefined,
  existing: ShopProductRequiredSource & { published?: boolean | null },
  update: ShopProductRequiredSource & { published?: boolean | null }
): string | null {
  if (!willShopProductBePublished(existing.published, update.published)) {
    return null;
  }

  if (!brands) {
    return `EAN ${ean}: Não foi possível validar a marca para publicação. Tente novamente.`;
  }

  const merged = mergeShopProductRequiredFields(existing, update);
  merged.brand = resolveBrandCategoryValue(brands, merged.brand);

  const invalidFields = getInvalidShopProductRequiredFields(merged, {
    forPublish: true,
    hasVariants: !!(update.hasVariants ?? existing.hasVariants)
  });
  if (!invalidFields.length) return null;

  return `EAN ${ean}: ${getShopProductPublishError(invalidFields)}`;
}
